import { ComponentProps, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Channel } from '@weave/schema-gen-ts/dist/schemas/comm-preference/shared/v1/enums.pb';
import { MessageType_Enum } from '@weave/schema-gen-ts/dist/schemas/messaging/shared/v1/enums.pb';
import { IdentifierType } from '@weave/schema-gen-ts/dist/schemas/notification/v1/notification_api.pb';
import { RelatedType, Status } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/enums.pb';
import { SMS } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/models.pb';
import {
  EscalationReason,
  ThreadAIOutcome,
  ThreadReadStatus,
  TriggerType,
} from '@weave/schema-gen-ts/dist/schemas/sms/shared/v2/enums.pb';
import { NotificationType } from '@weave/schema-gen-ts/dist/shared/notification/notifications.pb';
import { FlatIndexLocationWithAlign, ListRange, Virtuoso, VirtuosoHandle } from 'react-virtuoso';
import { CommPreferenceV1 } from '@frontend/api-comm-preference';
import { MediaQueries } from '@frontend/api-media';
import { NotificationQueries } from '@frontend/api-notifications';
import { SMSDataSharedUtils, SMSDataV3 } from '@frontend/api-sms-data';
import { getUser } from '@frontend/auth-helpers';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { getFullName } from '@frontend/name';
import { formatPhoneNumberE164 } from '@frontend/phone-numbers';
import { useScopedAppFlagStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { Button, SpinningLoader, Text, useDebouncedValue } from '@frontend/design-system';
import { useGetThreadData } from '../hooks/use-get-thread-data';
import { SMSContextActions } from '../types';
import { ScrollToBottomButton } from './scroll-to-bottom-button';
import { ThreadDateTimeDivider, BackgroundHighlight, SMSThreadItem } from './thread-items';
import { AiThreadItem } from './thread-items/ai-thread-item';
import { CallThreadItem } from './thread-items/call-thread-item';

const START_INDEX = 100000;
const NEW_WRITEBACK_UI_DELAY = 30000; // 30 seconds

export const DEFAULT_ENABLE_SMS_SELECTION_PREDICATE = (sms: SMS): boolean => {
  const hasError =
    (sms.status === Status.STATUS_NOT_SENT &&
      sms.statusDetails !== SMSDataV3.Types.OptimisticUpdateStatusDetails.SENDING) ||
    sms.status === Status.STATUS_ERROR;
  return !hasError;
};

type ScrollState = 'atBottom' | 'atTop' | 'inMiddle';
type VirtuosoContext = {
  hasOlderMessages: boolean;
  hasNewerMessages: boolean;
  isFetching: boolean;
  isLoading: boolean;
  isNewConversation: boolean;
  optOutBannerHeight?: number;
  mediaQueries: ReturnType<typeof MediaQueries.useMmsMedia>;
  focusThreadItem?: string;
  firstUnreadMessageId?: string;
  isSelecting?: boolean;
  selectedSmsIds?: string[];
  allIsSelected?: boolean;
  onToggleSmsSelection?: (smsId: string) => void;
  hiddenContextActions?: SMSContextActions[];
  hideWritebacks?: boolean;
  smsIdsWithNewWritebacks: string[];
  enableSmsSelectionPredicate: (sms: SMS) => boolean;
};

type ThreadListProps = {
  groupId: string;
  threadId: string;
  focusThreadItemData?: {
    id: string;
    createdAt: string;
    click?: number;
  };
  personPhone: string;
  onNewMessage?: () => void;
  unreadCount?: number;
  uiType: 'mini-chat' | 'inbox' | 'integrated-messaging';
  isSelecting?: boolean;
  selectedSmsIds?: string[];
  allIsSelected?: boolean;
  onToggleSmsSelection?: (smsId: string) => void;
  hiddenContextActions?: SMSContextActions[];
  hideWritebacks?: boolean;
  enableSmsSelectionPredicate?: (sms: SMS, defaultPredicate: (sms: SMS) => boolean) => boolean | undefined;
  mediaMaxWidth?: ComponentProps<typeof SMSThreadItem>['mediaMaxWidth'];
  trackingIdPrefix?: string;
  isNew?: boolean;
  /** Called when user clicks scroll-to-bottom while in a focused view with unloaded newer pages. Clears the focused SMS so the thread reloads from the latest page. */
  onResetFocusedView?: () => void;
};

export const ThreadList = ({
  groupId,
  threadId,
  focusThreadItemData,
  personPhone: providedPersonPhone,
  onNewMessage,
  unreadCount,
  uiType,
  isSelecting,
  selectedSmsIds,
  allIsSelected,
  onToggleSmsSelection,
  hiddenContextActions,
  hideWritebacks,
  enableSmsSelectionPredicate: providedSmsSelectionPredicate,
  mediaMaxWidth,
  trackingIdPrefix,
  isNew,
  onResetFocusedView,
  ...rest
}: ThreadListProps) => {
  const { t } = useTranslation('thread-body');
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const inboxFusionEnabled = getFeatureFlagValue('nwx:inbox-fusion');
  const markLatestMsgReadMutation = NotificationQueries.useMarkNotificationsAsRead();
  const [showBlockedThread, setShowBlockedThread] = useState(false);
  const setThreadStatusReadMutation = SMSDataV3.Mutations.useSetThreadsStatusReadMutation();

  const enableSmsSelectionPredicate = useCallback(
    (sms: SMS): boolean => {
      return (
        providedSmsSelectionPredicate?.(sms, DEFAULT_ENABLE_SMS_SELECTION_PREDICATE) ??
        DEFAULT_ENABLE_SMS_SELECTION_PREDICATE(sms)
      );
    },
    [providedSmsSelectionPredicate]
  );

  const user = getUser();

  const { isSmsMessageItem, isCallRecordItem, isAiActivityItem, getThreadItemId, getThreadPersonPhone } =
    SMSDataSharedUtils.threadItemsUtils;

  const {
    getThreadQuery,
    threadItems,
    latestSmsItem,
    firstItemId,
    firstUnreadMessageId,
    threadItemsDateList,
    contact,
  } = useGetThreadData({
    threadId,
    locationId: groupId,
    focusItemId: focusThreadItemData?.id,
    focusItemCreatedAt: focusThreadItemData?.createdAt,
    unreadCount: unreadCount,
    isNew,
  });

  const latestSMSId = latestSmsItem?.smsMessage.id;
  const personPhone = formatPhoneNumberE164(getThreadPersonPhone(threadItems[0]) || providedPersonPhone);
  const { data: isOptedOut } = CommPreferenceV1.Queries.useCheckPreferenceQuery({
    request: {
      userChannelAddress: personPhone,
      channel: Channel.CHANNEL_SMS,
      locationId: groupId,
      messageType: MessageType_Enum.MESSAGING_MANUAL,
    },
    options: {
      enabled: !!personPhone && !!groupId,
      select: (data) => data.consented === false,
    },
  });

  const latestMessageAutogeneratedBy = latestSmsItem?.smsMessage.autogeneratedBy;
  const isAutomated = useMemo(() => {
    if (!getThreadQuery.hasPreviousPage) {
      return undefined;
    }

    return latestMessageAutogeneratedBy === undefined ? undefined : !!latestMessageAutogeneratedBy;
  }, [getThreadQuery.hasPreviousPage, latestMessageAutogeneratedBy]);
  const markAsReadMutation = SMSDataV3.Mutations.useSetThreadsStatusReadMutation();
  const isUnread = getThreadQuery.data?.pages.some((page) =>
    ['new', ThreadReadStatus.THREAD_READ_STATUS_NEW].includes(page.thread.status)
  );
  const markAsRead = () => {
    if (!markAsReadMutation.isLoading && isUnread && !!user?.userID) {
      markAsReadMutation.mutate({
        threadIds: [threadId],
        locationId: groupId,
        groupIds: [groupId],
        userId: user.userID,
        _otherOptions: {
          automatedThreadIds: isAutomated ? [threadId] : [],
          manualThreadIds: isAutomated ? [] : [threadId],
        },
      });
    }
  };

  const handleMarkLatestMsgRead = (smsId: string) => {
    markLatestMsgReadMutation.mutate({
      notificationType: NotificationType.SMS,
      identifiers: [
        {
          key: 'uuid',
          val: smsId,
          type: IdentifierType.STRING,
        },
      ],
    });
  };

  const [debouncedUnreadMessageId, setDebouncedUnreadMessageId] = useState(firstUnreadMessageId);

  const smsIdsWithWritebacks = useMemo(
    () =>
      hideWritebacks
        ? []
        : threadItems.reduce<string[]>((acc, item) => {
            if (
              isSmsMessageItem(item) &&
              item.smsMessage.relatedIds.some(
                (relatedId) => relatedId.type === RelatedType.RELATED_TYPE_SMS_WRITEBACK_NOTE
              )
            ) {
              acc.push(item.smsMessage.id);
            }
            return acc;
          }, []),
    [threadItems, hideWritebacks]
  );
  const debouncedSmsIdsWithWritebacks = useDebouncedValue(smsIdsWithWritebacks, NEW_WRITEBACK_UI_DELAY);
  const smsIdsWithNewWritebacks = useMemo(
    () => smsIdsWithWritebacks.filter((smsId) => !debouncedSmsIdsWithWritebacks.includes(smsId)),
    [debouncedSmsIdsWithWritebacks, smsIdsWithWritebacks]
  );

  useEffect(() => {
    if (debouncedUnreadMessageId && !firstUnreadMessageId) {
      setTimeout(() => {
        setDebouncedUnreadMessageId(firstUnreadMessageId);
      }, 3000);
      return;
    }

    setDebouncedUnreadMessageId(firstUnreadMessageId);
  }, [firstUnreadMessageId]);

  const mediaIds = threadItems
    .filter(isSmsMessageItem)
    .flatMap((item) => (item.smsMessage.media ?? []).map(({ mediaId }) => mediaId));
  const mediaQueries = MediaQueries.useMmsMedia({ mediaIds, locationId: groupId });
  const [scrollState, setScrollState] = useState<ScrollState>(focusThreadItemData ? 'inMiddle' : 'atBottom');

  const [virtuosoListData, setVirtuosoListData] = useState<{
    firstItemId: string;
    firstItemIndex: number;
    listLength: number;
  }>({
    firstItemId,
    firstItemIndex: START_INDEX,
    listLength: threadItems.length,
  });
  const [hasInitiallyScrolled, setHasInitiallyScrolled] = useState(false);
  const [directionToFetch, setDirectionToFetch] = useState<'previous' | 'next'>();
  const someMediaIsLoading = Object.values(mediaQueries).some((mediaQuery) => mediaQuery.isLoading);
  const ref = useRef<VirtuosoHandle>(null);
  const scrollerRef = useRef<HTMLElement | null>(null);
  const isFarFromBottomRef = useRef(false);
  const [showScrollButton, setShowScrollButton] = useState(false);

  // For callRecords, getThreadItemId return threadCallId, whereas focusThreadItemData stores the channelId.
  // and since callRecords can either be a voicemail or a call, we need to match on both channelId and startedAt.
  const focusThreadItemIndexResult = threadItemsDateList.findIndex(
    (item) =>
      typeof item !== 'string' &&
      (isCallRecordItem(item)
        ? item.callRecord.channelId === focusThreadItemData?.id &&
          item.callRecord.startedAt === focusThreadItemData?.createdAt
        : getThreadItemId(item) === focusThreadItemData?.id)
  );

  // If the index search failed, fall back to 0 (first element) to avoid passing -1 to Virtuoso.
  // Returning 0 is safer than passing a negative index which causes react-virtuoso to throw.
  const focusThreadItemIndex = focusThreadItemIndexResult === -1 ? 0 : focusThreadItemIndexResult;

  const initialTopMostItemIndex: FlatIndexLocationWithAlign = {
    index: focusThreadItemData?.id ? focusThreadItemIndex : 'LAST',
    align: focusThreadItemData?.id ? 'center' : 'end',
    behavior: 'auto',
  };

  // Resolve the target id to the canonical threadItemId when possible so the
  // BackgroundHighlight (which compares context.focusThreadItem === threadItemId)
  // will match for callRecord items (where the incoming id may be a channelId).
  const matchedItem =
    focusThreadItemIndexResult >= 0 && typeof threadItemsDateList[focusThreadItemIndexResult] !== 'string'
      ? threadItemsDateList[focusThreadItemIndexResult]
      : undefined;

  const resolvedFocusThreadItem = matchedItem ? getThreadItemId(matchedItem) : focusThreadItemData?.id;

  const handlePageFetch = async () => {
    if (directionToFetch === 'previous') {
      await getThreadQuery.fetchPreviousPage();
    } else if (directionToFetch === 'next') {
      await getThreadQuery.fetchNextPage();
    }
  };

  const handleRangeChange = (range: ListRange) => {
    if (hasInitiallyScrolled) return;
    if (!focusThreadItemData) {
      // Wait for first page to finish rendering before setting initially scrolled to true
      setTimeout(() => {
        setHasInitiallyScrolled(true);
      }, 500);
    } else if (
      range.startIndex <= virtuosoListData.firstItemIndex + focusThreadItemIndex &&
      range.endIndex >= virtuosoListData.firstItemIndex + focusThreadItemIndex
    ) {
      // Wait for first page to finish rendering before setting initially scrolled to true
      setTimeout(() => {
        setHasInitiallyScrolled(true);
      }, 500);
    }
  };

  useEffect(() => {
    const firstItemHasChanged = virtuosoListData.firstItemId && firstItemId !== virtuosoListData.firstItemId;
    if (!firstItemHasChanged) {
      setVirtuosoListData((prev) => ({
        ...prev,
        firstItemId,
        listLength: threadItemsDateList.length,
      }));
    } else {
      const newItemsCount = threadItemsDateList.length - virtuosoListData.listLength;
      setVirtuosoListData((prev) => ({
        ...prev,
        listLength: threadItemsDateList.length,
        firstItemIndex: prev.firstItemIndex - newItemsCount,
      }));
    }
    // Cooldown so that multiple pages don't get fetched accidentally at the same time
    setTimeout(() => {
      setDirectionToFetch(undefined);
    }, 500);
  }, [threadItemsDateList.length]);

  useEffect(() => {
    setVirtuosoListData({
      firstItemId,
      firstItemIndex: START_INDEX,
      listLength: threadItemsDateList.length,
    });
    setDirectionToFetch(undefined);
  }, [threadId, focusThreadItemData?.id]);

  useEffect(() => {
    if (isUnread) {
      if (user?.userID)
        setThreadStatusReadMutation.mutateAsync({
          locationId: groupId,
          threadIds: [threadId],
          userId: user?.userID ?? '',
          _otherOptions: {
            automatedThreadIds: isAutomated ? [threadId] : [],
            manualThreadIds: isAutomated ? [] : [threadId],
          },
        });
      if (latestSMSId) {
        handleMarkLatestMsgRead(latestSMSId);
      }
    }
  }, [latestSMSId, isUnread]);

  useEffect(() => {
    if (!hasInitiallyScrolled && !!getThreadQuery.data && !someMediaIsLoading) {
      // use timeout here to allow the messages to finish rendering before initial scroll
      setTimeout(() => {
        ref.current?.scrollToIndex(initialTopMostItemIndex);
      }, 300);
    }
  }, [someMediaIsLoading, !!getThreadQuery.data]);

  useEffect(() => {
    if (!someMediaIsLoading && !!getThreadQuery.data) handlePageFetch();
  }, [!!getThreadQuery.data, directionToFetch]);

  useEffect(() => {
    if (firstItemId) {
      onNewMessage?.();
    }
  }, [firstItemId]);

  useEffect(() => {
    setShowBlockedThread(false);
    setHasInitiallyScrolled(false);
    isFarFromBottomRef.current = false;
    setShowScrollButton(false);
  }, [threadId]);

  useEffect(() => {
    if (focusThreadItemData?.click ?? 0 > 1) {
      ref.current?.scrollToIndex({ ...initialTopMostItemIndex, behavior: 'smooth' });
    }
  }, [focusThreadItemData?.click]);

  useEffect(() => {
    const initialIndexIsLast = initialTopMostItemIndex.index === 'LAST' || initialTopMostItemIndex.index < 0;
    if (focusThreadItemData?.id && focusThreadItemData.createdAt && initialIndexIsLast) {
      return;
    }

    if (hasInitiallyScrolled) {
      return;
    }

    const timeout = setTimeout(() => {
      ref.current?.scrollToIndex({ ...initialTopMostItemIndex, behavior: focusThreadItemData?.id ? 'smooth' : 'auto' });
    }, 300);

    return () => {
      clearTimeout(timeout);
    };
  }, [focusThreadItemData?.id, !!ref.current, initialTopMostItemIndex]);

  useEffect(() => {
    setHasInitiallyScrolled(false);
  }, [focusThreadItemData?.id, focusThreadItemData?.createdAt]);

  const prevLastAiActivityCreatedAt = useRef<string | undefined>();

  useEffect(() => {
    if (!inboxFusionEnabled) return;
    let timeout: NodeJS.Timeout;
    const lastItem = threadItems[threadItems.length - 1];
    const lastItemIsAiActivity = lastItem && isAiActivityItem(lastItem);
    if (!lastItemIsAiActivity) return;
    const lastItemCreatedAtChanged = lastItem?.aiActivity?.createdAt !== prevLastAiActivityCreatedAt.current;

    if (lastItemIsAiActivity && lastItemCreatedAtChanged) {
      timeout = setTimeout(() => {
        ref.current?.scrollToIndex({ index: 'LAST', align: 'start', behavior: 'auto' });
      }, 300);
      prevLastAiActivityCreatedAt.current = lastItem?.aiActivity?.createdAt;
    }

    prevLastAiActivityCreatedAt.current = lastItem?.aiActivity?.createdAt;

    return () => clearTimeout(timeout);
  }, [threadItems, isAiActivityItem, focusThreadItemData]);

  SMSDataSharedUtils.useReportTimeToOpenToPendo(
    threadItems.filter(isSmsMessageItem).at(-1)?.smsMessage,
    uiType,
    !!getThreadQuery.hasPreviousPage || getThreadQuery.isLoading
  );

  const debouncedHasPreviousPage = useDebouncedValue(getThreadQuery.hasPreviousPage, 500);

  const filteredThreadItemsDateList = inboxFusionEnabled
    ? threadItemsDateList.filter((threadItem) => {
        if (typeof threadItem === 'string') return true;
        if (!isAiActivityItem(threadItem)) return true;
        const aiItem = threadItem.aiActivity;
        // once BE schema is finalized, part will be moved to a util to check if ai activity should be shown
        return !(
          (aiItem.triggerType === TriggerType.TRIGGER_TYPE_BOT_OUTCOME &&
            aiItem.status === ThreadAIOutcome.THREAD_AI_OUTCOME_UNSPECIFIED) ||
          aiItem.triggerType === TriggerType.TRIGGER_TYPE_BOT_DEESCALATED ||
          aiItem.escalationReason === EscalationReason.ESCALATION_REASON_CONFIRM_CANCEL ||
          aiItem.escalationReason === EscalationReason.ESCALATION_REASON_CONFIRM_RESCHEDULE ||
          aiItem.escalationReason === EscalationReason.ESCALATION_REASON_CONFIRM_BOOKING
        );
      })
    : threadItemsDateList;

  const hasNewerMessages = !!getThreadQuery.hasPreviousPage;
  const showScrollToBottom =
    showScrollButton && hasInitiallyScrolled && !getThreadQuery.isLoading && !someMediaIsLoading;

  const handleScroll = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    const isScrollFar = distanceFromBottom > 150;
    if (isFarFromBottomRef.current !== isScrollFar) {
      isFarFromBottomRef.current = isScrollFar;
      setShowScrollButton(isScrollFar);
    }
  }, []);

  const handleScrollToBottom = useCallback(() => {
    if (hasNewerMessages && onResetFocusedView) {
      // Newer pages exist that haven't been loaded — reset the focused view
      // so the thread query re-fetches from the latest page and positions at bottom.
      onResetFocusedView();
    } else {
      ref.current?.scrollToIndex({ index: 'LAST', align: 'end', behavior: 'smooth' });
    }
  }, [hasNewerMessages, onResetFocusedView]);

  if (getThreadQuery.data?.pages[0]?.thread.isBlocked && !showBlockedThread) {
    return <BlockedMessageContent show={() => setShowBlockedThread(true)} />;
  }

  return (
    <div css={{ position: 'relative', display: 'flex', flexDirection: 'column', flexGrow: 1, minHeight: 0 }}>
      <Virtuoso<(typeof threadItemsDateList)[number], VirtuosoContext>
        key={`${threadId}-${focusThreadItemData?.id ?? ''}`}
        ref={ref}
        scrollerRef={(el) => {
          if (!el || scrollerRef.current === el) return;
          scrollerRef.current?.removeEventListener('scroll', handleScroll);
          scrollerRef.current = el as HTMLElement;
          el.addEventListener('scroll', handleScroll, { passive: true });
        }}
        style={{
          height: 'auto',
          flexGrow: 1,
          opacity: isOptedOut ? 0.5 : 1,
        }}
        data-trackingid={`${trackingIdPrefix}-thread-list`}
        alignToBottom
        followOutput={(isAtBottom) => {
          if (focusThreadItemData?.id && focusThreadItemData.createdAt && !hasInitiallyScrolled) {
            return false;
          }
          if (isAtBottom && !debouncedHasPreviousPage) return 'smooth';
          return false;
        }}
        firstItemIndex={virtuosoListData.firstItemIndex}
        data={filteredThreadItemsDateList}
        startReached={() => {
          // Only fetch older pages after initial scroll has completed; prevents unwanted prefetch when focusing thread items
          if (hasInitiallyScrolled && getThreadQuery.hasNextPage) setDirectionToFetch('next');
        }}
        atBottomThreshold={25}
        atTopStateChange={(isAtTop) => {
          setScrollState(isAtTop ? 'atTop' : 'inMiddle');
          if (isAtTop && hasInitiallyScrolled) {
            setDirectionToFetch('next');
          }
        }}
        atBottomStateChange={(isAtBottom) => {
          setScrollState(isAtBottom ? 'atBottom' : 'inMiddle');
          if (isAtBottom && hasInitiallyScrolled) {
            setDirectionToFetch('previous');
            markAsRead();
          }
        }}
        onFocus={() => {
          if (scrollState === 'atBottom') {
            markAsRead();
          }
        }}
        // Layout shifts when selecting items make it necessary to increase the viewport to allow messages to completely
        // leave viewport before being unrendered
        increaseViewportBy={isSelecting ? 150 : 0}
        context={{
          hasOlderMessages: !!getThreadQuery.hasNextPage,
          hasNewerMessages: !!getThreadQuery.hasPreviousPage,
          isFetching: getThreadQuery.isFetching,
          isNewConversation: !getThreadQuery.isFetching && !threadItems.length,
          isLoading: (!getThreadQuery.data && !getThreadQuery.isError) || getThreadQuery.isLoading,
          mediaQueries,
          focusThreadItem: resolvedFocusThreadItem,
          firstUnreadMessageId: debouncedUnreadMessageId,
          isSelecting,
          selectedSmsIds,
          allIsSelected,
          onToggleSmsSelection,
          hiddenContextActions,
          smsIdsWithNewWritebacks,
          enableSmsSelectionPredicate,
        }}
        rangeChanged={handleRangeChange}
        components={{
          Header: ({ context }) => {
            if (context?.isLoading && !context?.isNewConversation) {
              return (
                <div
                  css={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: theme.spacing(1, 0),
                    width: '100%',
                  }}
                >
                  <SpinningLoader size='small' />
                </div>
              );
            }

            if (context?.hasOlderMessages && context?.isFetching && !context?.isLoading) {
              return (
                <div
                  css={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: theme.spacing(1, 0),
                    width: '100%',
                  }}
                >
                  <SpinningLoader size='small' />
                </div>
              );
            }
            if (!context?.isNewConversation && !context?.hasOlderMessages) {
              return (
                <Text css={{ margin: 0, padding: theme.spacing(2, 0), textAlign: 'center' }}>
                  🎉 {t('You have reached the end')}
                </Text>
              );
            }
            return null;
          },
          Footer: ({ context }) => {
            if (context?.isNewConversation)
              return (
                <Text css={{ margin: 0, padding: theme.spacing(2, 0), textAlign: 'center' }}>
                  {t('No messages yet.')}
                </Text>
              );

            if (context?.hasNewerMessages && context?.isFetching && !context?.isLoading)
              return (
                <div
                  css={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: theme.spacing(1, 0),
                    width: '100%',
                  }}
                >
                  <SpinningLoader size='small' />
                </div>
              );

            return null;
          },
        }}
        itemContent={(index, threadItem, context) => {
          if (typeof threadItem === 'string') {
            return <ThreadDateTimeDivider key={threadItem} timestamp={new Date(threadItem)} />;
          }

          const threadItemId = getThreadItemId(threadItem);
          const mediaIds = isSmsMessageItem(threadItem)
            ? (threadItem.smsMessage.media ?? []).map(({ mediaId }) => mediaId)
            : [];
          const media = context.mediaQueries.reduce<NonNullable<(typeof context.mediaQueries)[number]['data']>[]>(
            (acc, { data }) => {
              if (!data || !mediaIds.includes(data.mediaId)) return acc;
              acc.push(data);
              return acc;
            },
            []
          );

          return (
            <>
              {context?.firstUnreadMessageId && threadItemId === context.firstUnreadMessageId && (
                <div
                  css={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: theme.spacing(1),
                    width: '100%',
                    padding: theme.spacing(0, 1, 2),
                  }}
                >
                  <figure css={{ height: 1, backgroundColor: theme.colors.primary20, flexGrow: 1 }} />
                  <Text size='small' color='primary' textAlign='center'>
                    {t('New')}
                  </Text>
                  <figure css={{ height: 1, backgroundColor: theme.colors.primary20, flexGrow: 1 }} />
                </div>
              )}
              <div
                css={{
                  position: 'relative',
                }}
                data-trackingid={`${trackingIdPrefix}-thread-item-${threadItemId}`}
              >
                {context?.focusThreadItem === threadItemId && (
                  <BackgroundHighlight
                    additionalCSS={{
                      default: {
                        top: theme.spacing(-1),
                        bottom: theme.spacing(1),
                      },
                    }}
                    css={{
                      zIndex: theme.zIndex.background,
                    }}
                  />
                )}
                {isSmsMessageItem(threadItem) && (
                  <SMSThreadItem
                    key={threadItemId + index}
                    sms={threadItem.smsMessage}
                    avatarProps={{
                      size: 'small',
                    }}
                    media={media}
                    css={{
                      padding: theme.spacing(0, 2),
                    }}
                    contactName={{
                      name: getFullName(contact ?? {}),
                      firstName: contact?.firstName,
                      lastName: contact?.lastName,
                    }}
                    threadPersonId={contact?.personId}
                    isSelecting={context.isSelecting}
                    isSelected={
                      context.enableSmsSelectionPredicate(threadItem.smsMessage)
                        ? context.allIsSelected || context.selectedSmsIds?.includes(threadItem.smsMessage.id)
                        : false
                    }
                    onToggleSmsSelection={() => context.onToggleSmsSelection?.(threadItem.smsMessage.id)}
                    hasNewWriteback={context.smsIdsWithNewWritebacks.includes(threadItem.smsMessage.id)}
                    selectionDisabled={
                      !context.enableSmsSelectionPredicate(threadItem.smsMessage) || !!threadItem.smsMessage.deletedAt
                    }
                    mediaMaxWidth={mediaMaxWidth}
                    hiddenContextActions={context.hiddenContextActions}
                    hideWritebacks={hideWritebacks}
                  />
                )}
                {isCallRecordItem(threadItem) && (
                  <CallThreadItem
                    callItem={threadItem.callRecord}
                    // Voicemails and mini-chat should have no context actions.
                    hiddenContextActions={
                      uiType === 'mini-chat' ? context.hiddenContextActions : ['delete', 'copy', 'tasks']
                    }
                    avatarProps={{
                      size: 'small',
                    }}
                    contact={contact}
                    threadId={threadId}
                  />
                )}
                {isAiActivityItem(threadItem) && inboxFusionEnabled && <AiThreadItem aiItem={threadItem.aiActivity} />}
              </div>
            </>
          );
        }}
        initialTopMostItemIndex={initialTopMostItemIndex}
        {...rest}
      />
      <ScrollToBottomButton
        visible={showScrollToBottom}
        onClick={handleScrollToBottom}
        trackingIdPrefix={trackingIdPrefix}
      />
    </div>
  );
};

const BlockedMessageContent = ({ show }: { show: () => void }) => {
  const { t } = useTranslation('messages');
  return (
    <section
      css={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        justifyContent: 'center',
        alignItems: 'center',
        gap: theme.spacing(1),
      }}
    >
      <Icon name='block' size={112} />
      <Text>{t('This sender was blocked by your organization')}</Text>
      <Button variant='tertiary' onClick={show}>
        {t('View messages anyway')}
      </Button>
    </section>
  );
};
